import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createUniform,
  getAllUniform,
  getUniformByID,
  UniformImageUpload,
  updateUniform,
} from "./services/uniform.services";
import { clearUniformDetails } from "./uniformSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { RxCross1 } from "react-icons/rx";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";
import { getAllActiveIndustries } from "../industry/services/industry.services";
import { IIndustry } from "../industry/industryModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";

const CreateUniform: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  // const location = useLocation();
  // const UniformID = location.state?.UniformID;
  const UniformID = useAppSelector((state) => state.muidrawer.data);
  const UniformDetail = useAppSelector((state) => state.uniform.UniformDetail);
  const loading = useAppSelector((state) => state.uniform.formLoading);
  const IndustryList = useAppSelector((state) => state.industry.IndustryList);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (UniformID) {
      dispatch(getUniformByID(UniformID));
    }
    dispatch(getAllActiveIndustries());
    return () => {
      dispatch(clearUniformDetails());
    };
  }, [UniformID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [UniformDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      IndustriesID: values?.IndustriesID ? +values?.IndustriesID : null,
      Title: values?.Title ? values?.Title : "",
      Description: values?.Description ? values?.Description : "",
      SequenceNo: values?.SequenceNo ? +values?.SequenceNo : null,
      IsActive: values?.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image &&
      typeof values?.Image !== "string" &&
      Array.isArray(values?.Image) &&
      values?.Image?.length > 0
        ? values.Image[0].getRawFile()
        : "";

    try {
      if (UniformID) {
        const editPayload = {
          ...formData,
          ID: UniformID,
        };

        const response = await dispatch(updateUniform(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());

          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            Array.isArray(values?.Image) &&
            values?.Image?.length > 0
          ) {
            ImageData.append("Image", Image);
            ImageData.append("ID", UniformID);
            await dispatch(UniformImageUpload(ImageData));
          }
          dispatch(getAllUniform());
          dispatch(clearUniformDetails());
        }
      } else {
        const response = await dispatch(createUniform(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          if (
            values?.Image &&
            typeof values?.Image !== "string" &&
            Array.isArray(values?.Image) &&
            values?.Image?.length > 0 &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Image", Image);

            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(UniformImageUpload(ImageData));
          }
          dispatch(getAllUniform());
          dispatch(clearUniformDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={UniformDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
                <GridLayoutItem className="master-drawer-header">
                  <Typography.h4 className="m-0">
                    {UniformID ? "Update Uniform" : "Create Uniform"}
                  </Typography.h4>
                  <Button
                    type="button"
                    fillMode={"flat"}
                    themeColor={"dark"}
                    style={{ marginRight: 4 }}
                    onClick={() => dispatch(closed())}
                  >
                    <RxCross1 />
                  </Button>
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IndustriesID`}
                    label="Industry"
                    placeholder="Industry"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={IndustryList?.map((industry: IIndustry) => {
                      return {
                        value: industry?.ID,
                        label: industry?.Title,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="Description"
                    label="Description"
                    component={FormRichTextField}
                    style={{ height: 120 }}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    wrapperClassName="right-aligned-field"
                    name="SequenceNo"
                    label="Sequence No."
                    placeholder="0"
                    format="n0"
                    component={FormNumericTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    justifyContent="start"
                    name={`Image`}
                    label="Uniform Image"
                    type="file"
                    accept="image/*"
                    alternateName="Uniform Image"
                    component={FormImageUploadMorden}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: "20px",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={UniformID ? "Update" : "Create"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => dispatch(closed())}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateUniform;
